import React, { useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { Filter, Columns3, X } from "lucide-react";

// Search + Filter + Sort + Group-by + View switcher. All client-side; the
// Board page passes the current values and applies them via a memoized
// derivation on top of `items`.
//
// Filter model: { col_id, label_ids[] } — narrows items whose status cell
// value is in label_ids. Multiple filters AND together (Monday's behavior).
//
// Sort model: { col_id, dir } where dir = "asc" | "desc". `col_id = null`
// means "no sort" (use the natural group-by-order ordering).
//
// Group-by model: `null` (use board groups) or a status column id (one
// virtual group per label value). Affects table layout AND kanban columns.
export default function BoardToolbar({
  board,
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  groupBy,
  onGroupByChange,
  view,
  onViewChange,
  sysCols = [],
  onSysColsChange,
  systemColDefs = [],
}) {
  const columns = (board?.columns || []).slice().sort((a, z) => a.order - z.order);
  const statusColumns = columns.filter((c) => c.type === "status");

  const [filterOpen, setFilterOpen] = useState(false);

  const removeFilter = (idx) =>
    onFiltersChange(filters.filter((_, i) => i !== idx));

  const addFilter = (col) => {
    if (filters.some((f) => f.col_id === col._id)) return;
    onFiltersChange([...filters, { col_id: col._id, label_ids: [] }]);
    setFilterOpen(false);
  };

  const updateFilter = (idx, label_ids) =>
    onFiltersChange(filters.map((f, i) => (i === idx ? { ...f, label_ids } : f)));

  return (
    <div className="board-toolbar">
      <div className="toolbar-left">
        <input
          type="search"
          className="toolbar-search"
          placeholder="Search items..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        <Dropdown show={filterOpen} onToggle={(o) => setFilterOpen(o)}>
          <Dropdown.Toggle as="button" className="toolbar-btn" id="filter-btn">
            <span className="btn-icon"><Filter size={12} /></span> Filter
            {filters.length > 0 && <span className="toolbar-count">{filters.length}</span>}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {statusColumns.length === 0 && (
              <Dropdown.ItemText className="muted">No status columns to filter on</Dropdown.ItemText>
            )}
            {statusColumns.map((c) => (
              <Dropdown.Item key={c._id} onClick={() => addFilter(c)}>
                {c.title}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown>
          <Dropdown.Toggle as="button" className="toolbar-btn" id="sort-btn">
            <span className="btn-icon">↕</span> Sort
            {sort?.col_id && <span className="toolbar-count">1</span>}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => onSortChange({ col_id: null, dir: "asc" })}>
              <em>Default order</em>
            </Dropdown.Item>
            <Dropdown.Divider />
            {columns.map((c) => (
              <React.Fragment key={c._id}>
                <Dropdown.Item onClick={() => onSortChange({ col_id: c._id, dir: "asc" })}>
                  {c.title} ↑
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onSortChange({ col_id: c._id, dir: "desc" })}>
                  {c.title} ↓
                </Dropdown.Item>
              </React.Fragment>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown>
          <Dropdown.Toggle as="button" className="toolbar-btn" id="group-btn">
            <span className="btn-icon">⊞</span> Group by
            {groupBy && <span className="toolbar-count">1</span>}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => onGroupByChange(null)}>
              <em>Board groups (default)</em>
            </Dropdown.Item>
            <Dropdown.Divider />
            {statusColumns.map((c) => (
              <Dropdown.Item key={c._id} onClick={() => onGroupByChange(c._id)}>
                {c.title}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {systemColDefs.length > 0 && (
          <Dropdown autoClose="outside">
            <Dropdown.Toggle as="button" className="toolbar-btn" id="columns-btn">
              <span className="btn-icon"><Columns3 size={12} /></span> Columns
              {sysCols.length > 0 && <span className="toolbar-count">{sysCols.length}</span>}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>System columns</Dropdown.Header>
              {systemColDefs.map((c) => {
                const checked = sysCols.includes(c.system);
                return (
                  <Dropdown.Item
                    key={c._id}
                    as="button"
                    onClick={(e) => {
                      e.preventDefault();
                      const next = checked
                        ? sysCols.filter((s) => s !== c.system)
                        : [...sysCols, c.system];
                      onSysColsChange && onSysColsChange(next);
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={checked}
                      readOnly
                      inline
                      label={c.title}
                    />
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      <div className="toolbar-right">
        <div className="view-switcher">
          {["table", "kanban", "calendar", "timeline"].map((v) => (
            <button
              key={v}
              type="button"
              className={`view-tab ${view === v ? "active" : ""}`}
              onClick={() => onViewChange(v)}
            >
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {filters.length > 0 && (
        <div className="filter-chips">
          {filters.map((f, idx) => {
            const col = columns.find((c) => c._id === f.col_id);
            if (!col) return null;
            return (
              <FilterChip
                key={f.col_id}
                column={col}
                labelIds={f.label_ids}
                onChange={(ids) => updateFilter(idx, ids)}
                onRemove={() => removeFilter(idx)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterChip({ column, labelIds, onChange, onRemove }) {
  const [open, setOpen] = useState(labelIds.length === 0);
  const labels = column.settings?.labels || [];
  const summary =
    labelIds.length === 0
      ? "any"
      : labelIds
          .map((id) => labels.find((l) => l.id === id)?.label || id)
          .join(", ");

  return (
    <div className="filter-chip">
      <span className="chip-col">{column.title}:</span>
      <Dropdown show={open} onToggle={(o) => setOpen(o)} className="chip-dropdown">
        <Dropdown.Toggle as="button" className="chip-summary">
          {summary}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {labels.map((l) => {
            const checked = labelIds.includes(l.id);
            return (
              <Dropdown.Item
                key={l.id}
                as="button"
                onClick={(e) => {
                  e.preventDefault();
                  const next = checked
                    ? labelIds.filter((x) => x !== l.id)
                    : [...labelIds, l.id];
                  onChange(next);
                }}
              >
                <Form.Check
                  type="checkbox"
                  checked={checked}
                  readOnly
                  inline
                  label={
                    <span className="chip-label-row">
                      <span className="chip-swatch" style={{ background: l.color }} />
                      {l.label}
                    </span>
                  }
                />
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
      <button className="chip-x" onClick={onRemove} title="Remove filter" aria-label="Remove filter"><X size={10} /></button>
    </div>
  );
}
